import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { ExtranetService } from './extranet.service';
import { IdentityService } from './identity.service';
import { ExtranetRole, GroupPaymentData, PropertyGroupResponse } from '../models/extranet.model';

export type GroupContextStatus = 'idle' | 'loading' | 'ready' | 'unavailable';

/**
 * The caller's own property group, shared app-wide (F0, WI #3296): the currency every money
 * figure prints in, and the issuer/bank details a voucher or statement letterhead carries.
 * Loaded lazily — the first `ensureLoaded()` fires GET /extranet/access/groups/mine, later calls
 * are no-ops — because most screens never need it and admins have no group at all (the BE 404s
 * for a caller with no active membership, which lands as `group() === null`, `unavailable`).
 *
 * The load waits for the identity: while `activeRole()` is still null a request is only NOTED
 * and fires once GET /extranet/access/me has answered, so an admin whose identity arrives late
 * never hits the BE and never gets stuck on a spurious `unavailable`. An identity change
 * afterwards — the dev "View as" role switch, or the admin's "open as this group" viewpoint
 * (F7) — resets the group and refetches if anyone asked for it.
 *
 * View-as (handoff §17, AC-17.3): the identity is keyed on role AND `me.groupId`, and
 * `GET /groups/mine` follows `X-Extranet-View-As-Group` server-side, so while an admin views
 * the product as a tenant this service holds THAT group — its currency, its issuer and bank
 * details — and every consumer (money figures, vouchers, statements, the banner) reads the
 * viewed tenant without knowing about the override.
 */
@Injectable({ providedIn: 'root' })
export class GroupContextService {
  private readonly extranetSvc = inject(ExtranetService);
  private readonly identity = inject(IdentityService);

  readonly group = signal<PropertyGroupResponse | null>(null);
  readonly status = signal<GroupContextStatus>('idle');

  /** Group currency, 'EUR' until the group is known (matches the platform default). */
  readonly currency = computed(() => this.group()?.currency || 'EUR');
  /** Issuer bank details (Settings › Payment data); null until loaded or when never filled. */
  readonly payment = computed<GroupPaymentData | null>(() => this.group()?.payment ?? null);
  /** True once an IBAN is on file — the Banking "no IBAN" warning keys off this. */
  readonly hasIban = computed(() => !!this.payment()?.iban);
  readonly loading = computed(() => this.status() === 'loading');

  /** Someone called ensureLoaded()/reload(): a load is owed as soon as the role is known. */
  private wanted = false;
  /** Bumped per request so a response from a superseded role is dropped. */
  private seq = 0;
  /** Seeded synchronously so the effect's first run is a no-op unless the identity really moved. */
  private lastKey: string | null = GroupContextService.keyOf(this.identity.activeRole(), this.identity.me()?.groupId ?? null);

  constructor() {
    // Effects run on the next change detection, not at construction: seeding `lastKey` above
    // keeps a group loaded in between from being thrown away on that first run.
    effect(() => {
      const key = GroupContextService.keyOf(this.identity.activeRole(), this.identity.me()?.groupId ?? null);
      untracked(() => this.onIdentity(key));
    });
  }

  /** Role + group: a view-as pin changes the group without necessarily changing the role. */
  private static keyOf(role: ExtranetRole | null, groupId: string | null): string | null {
    return role === null ? null : `${role}:${groupId ?? ''}`;
  }

  /** Load once; safe to call from every screen that needs the group. Admins never hit the BE. */
  ensureLoaded(): void {
    this.wanted = true;
    if (this.status() !== 'idle') return;
    this.fetch();
  }

  /** Force a refetch (after Settings › Property group / Payment data are saved). */
  reload(): void {
    this.wanted = true;
    if (this.loading()) return;
    this.fetch();
  }

  private onIdentity(key: string | null): void {
    if (key === this.lastKey) return;
    this.lastKey = key;
    // Whatever was loaded belonged to the previous identity: start over, and honour a pending request.
    this.seq++;
    this.group.set(null);
    this.status.set('idle');
    if (this.wanted) this.fetch();
  }

  private fetch(): void {
    const role = this.identity.activeRole();
    if (role === null) return;                    // identity pending: stays idle, onIdentity() fires it
    const id = ++this.seq;
    if (role === 'ADMIN') {
      this.group.set(null);
      this.status.set('unavailable');
      return;
    }
    this.status.set('loading');
    this.extranetSvc.getMyGroup().subscribe({
      next: g => { if (id !== this.seq) return; this.group.set(g); this.status.set('ready'); },
      error: () => { if (id !== this.seq) return; this.group.set(null); this.status.set('unavailable'); },
    });
  }
}
